import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuthGuard } from '../core/services/authGuard';
import { RoleGuard } from '../core/services/roleGuard';

@NgModule({
  imports: [
    RouterModule.forRoot(
      [
        /* define app module routes here, e.g., to lazily load a module
     (do not place feature module routes here, use an own -routing.module.ts in the feature instead)
     */
        {
          path: '',
          pathMatch: 'full',
          loadChildren: () =>
            import('./buyflow-home/buyflow-home.module').then(
              (m) => m.BuyflowHomeModule,
            ),
        },
        {
          path: 'home',
          redirectTo: '',
          pathMatch: 'full',
        },
        {
          path: 'sign-up/confirm-email',
          loadChildren: () =>
            import('./sign-up/confirm-email/confirm-email.module').then(
              (m) => m.ConfirmEmailModule,
            ),
        },
        {
          path: 'sign-up/confirm-phone',
          loadChildren: () =>
            import('./sign-up/confirm-phone/confirm-phone.module').then(
              (m) => m.ConfirmPhoneModule,
            ),
        },
        {
          path: 'auth-callback',
          loadChildren: () =>
            import('./auth/auth-module.module').then((m) => m.AuthModuleModule),
        },
        {
          path: 'rental-location-map/:waterBodyName',
          loadChildren: () =>
            import(
              './public/charter-buyflow/charter-buyflow-vessels/charter-buyflow-vessels.module'
            ).then((m) => m.CharterBuyflowVesselsModule),
        },
        {
          path: 'charters/yacht-and-boat-rentals/:waterBodyName',
          loadChildren: () =>
            import('./seo/seo.module').then((m) => m.SeoModule),
        },
        {
          path: 'charters/boat-and-yacht-rentals/:waterBodyName',
          loadChildren: () =>
            import('./seo/seo.module').then((m) => m.SeoModule),
        },
        {
          path: 'vessels/yacht-and-boat-rentals/:uniqueVesselString',
          loadChildren: () =>
            import(
              './public/charter-buyflow/charter-buyflow-vessel-detail/charter-buyflow-vessel-detail.module'
            ).then((m) => m.CharterBuyflowVesselDetailModule),
        },
        {
          path: 'vessels/yacht-and-boat-rentals/oauth/confirmed',
          loadChildren: () =>
            import(
              './public/charter-buyflow/charter-buyflow-vessel-detail/charter-buyflow-vessel-detail.module'
            ).then((m) => m.CharterBuyflowVesselDetailModule),
        },
        {
          path: 'vessels/yacht-and-boat-rentals/:affiliateVanity/:uniqueVesselString',
          loadChildren: () =>
            import(
              './public/charter-buyflow/charter-buyflow-vessel-detail/charter-buyflow-vessel-detail.module'
            ).then((m) => m.CharterBuyflowVesselDetailModule),
        },
        {
          path: 'customer/booking/:vesselId/:marketingId',
          loadChildren: () =>
            import(
              './public/charter-buyflow/charter-buyflow-customer-booking/charter-buyflow-customer-booking.module'
            ).then((m) => m.CharterBuyflowCustomerBookingModule),
        },
        {
          path: 'customer/booking/:affiliateVanity/:vesselId/:marketingId',
          loadChildren: () =>
            import(
              './public/charter-buyflow/charter-buyflow-customer-booking/charter-buyflow-customer-booking.module'
            ).then((m) => m.CharterBuyflowCustomerBookingModule),
        },
        {
          path: 'booking/:id',
          loadChildren: () =>
            import('./portal/bookings/booking/booking.module').then(
              (m) => m.BookingModule,
            ),
        },
        {
          path: 'manage',
          loadChildren: () =>
            import('./portal/portal.module').then((m) => m.PortalModule),
        },
        {
          // redirect boat owners from old availability page to bookings manager
          path: 'boatowner/:publicId',
          redirectTo: '/manage/bookings-manager?activeTab=approved',
        },
        {
          path: 'contact-anchor',
          loadChildren: () =>
            import('./contact-anchor/contact-anchor.module').then(
              (m) => m.ContactAnchorModule,
            ),
        },
        {
          path: 'become-a-captain',
          loadChildren: () =>
            import(
              './public/become-a-captain-landing/become-a-captain-landing.module'
            ).then((m) => m.BecomeACaptainLandingModule),
        },
        {
          path: 'list-my-boat',
          loadChildren: () =>
            import('./public/list-my-boat/list-my-boat.module').then(
              (m) => m.ListMyBoatModule,
            ),
        },
        {
          path: 'login',
          loadChildren: () =>
            import('./login/login.module').then((m) => m.LoginModule),
        },
        {
          path: 'passenger-faq',
          loadChildren: () =>
            import('./passenger-faq/passenger-faq.module').then(
              (m) => m.PassengerFaqModule,
            ),
        },
        {
          path: 'retry-payment/:paymentIntent',
          loadChildren: () =>
            import(
              './portal/charters/charter-retry-payment/charter-retry-payment.module'
            ).then((m) => m.CharterRetryPaymentModule),
          canActivate: [AuthGuard, RoleGuard],
          data: { roles: ['Customer'] },
        },
        {
          path: 'charters/:charterId/payments/:paymentId',
          loadChildren: () =>
            import(
              './portal/charters/charter-payment/charter-payment.module'
            ).then((m) => m.CharterPaymentModule),
          canActivate: [AuthGuard, RoleGuard],
          data: { roles: ['Customer'] },
        },
        {
          path: 'charters/:charterId/payments/:paymentId/:sessionToken',
          loadChildren: () =>
            import(
              './portal/charters/charter-payment/customer-verification/customer-verification.module'
            ).then((m) => m.CustomerVerificationModule),
        },
        {
          path: 'customer/charter/:charterId/:paymentId/agreement/:sessionToken',
          loadChildren: () =>
            import(
              './portal/charters/charter-payment/customer-verification/customer-verification.module'
            ).then((m) => m.CustomerVerificationModule),
        },
        {
          path: 'status',
          loadChildren: () =>
            import('./versioning/status-info/status-info.module').then(
              (m) => m.StatusInfoModule,
            ),
        },
        {
          path: 'captain-faq',
          loadChildren: () =>
            import('./captain-faq/captain-faq.module').then(
              (m) => m.CaptainFaqModule,
            ),
        },
        {
          path: 'boat-owner-faq',
          loadChildren: () =>
            import('./boat-owner-faq/boat-owner-faq.module').then(
              (m) => m.BoatOwnerFaqModule,
            ),
        },
        {
          path: 'cookie-statement',
          loadChildren: () =>
            import('./public/cookie-statement/cookies-statement.module').then(
              (m) => m.CookiesStatementModule,
            ),
        },
        {
          path: 'customer',
          loadChildren: () =>
            import('./customer/customer.module').then((m) => m.CustomerModule),
        },
        {
          path: 'users/reset-password/:resetPasswordToken',
          loadChildren: () =>
            import('./password/reset-password/reset-password.module').then(
              (m) => m.ResetPasswordModule,
            ),
        },
        {
          path: 'login/forgot-password',
          loadChildren: () =>
            import('./password/forgot-password/forgot-password.module').then(
              (m) => m.ForgotPasswordModule,
            ),
        },
        {
          path: 'affiliate-dashboard',
          loadChildren: () =>
            import(
              './affiliate/affiliate-dashboard/affiliate-dashboard.module'
            ).then((m) => m.AffiliateDashboardModule),
          // canActivate: [AuthGuard, RoleGuard],
          // data: { roles: ['Customer'] },
        },
        {
          path: 'sign-up/become-a-boat-owner/vessels',
          loadChildren: () =>
            import(
              './sign-up/boat-owner/become-a-boat-owner/become-a-boat-owner.module'
            ).then((m) => m.BecomeABoatOwnerModule),
        },
        {
          path: 'sign-up/anchor-code',
          loadChildren: () =>
            import('./sign-up/anchor-code/anchor-code.module').then(
              (m) => m.AnchorCodeModule,
            ),
        },
        {
          path: 'sign-up/welcome-to-anchor',
          loadChildren: () =>
            import('./sign-up/welcome-to-anchor/welcome-to-anchor.module').then(
              (m) => m.WelcomeToAnchorModule,
            ),
        },
        {
          path: 'privacy-policy',
          loadChildren: () =>
            import('./public/mobile-privacy/mobile-privacy.module').then(
              (m) => m.MobilePrivacyModule,
            ),
        },
        {
          path: 'terms-of-service',
          redirectTo: 'terms-of-use',
          pathMatch: 'full',
        },
        {
          path: 'terms-of-use',
          loadChildren: () =>
            import('./public/mobile-tos/mobile-tos.module').then(
              (m) => m.MobileTosModule,
            ),
        },
        {
          path: 'charters/boat-unavailable',
          loadChildren: () =>
            import(
              './customer/customer-boat-unavailable/customer-boat-unavailable.module'
            ).then((m) => m.CustomerBoatUnavailableModule),
        },
        {
          path: 'become-an-affiliate',
          loadChildren: () =>
            import('./affiliate/affiliate-signup/affiliate-signup.module').then(
              (m) => m.AffiliateSignupModule,
            ),
        },
        {
          path: 'affiliate-paystubs',
          loadChildren: () =>
            import(
              './portal/profile/bank-information/bank-information.module'
            ).then((m) => m.BankInformationModule),
          canActivate: [AuthGuard, RoleGuard],
          data: { roles: ['Customer'] },
        },
      ],
      {
        initialNavigation: 'enabled',
        enableTracing: false,
      },
    ),
  ],
  exports: [RouterModule],
  providers: [],
})
export class AppRoutingModule {}
